import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { DEFAULT_LIMIT, DEFAULT_OFFSET, LicenseType, LINKS } from '@trip-ux/constants';
import { apiClient, apiClientForDownload } from '@trip-ux/redux-store';
import { RootStateOrAny } from 'react-redux';
import {
  License,
  LICENSE_TABLE_LENGTH_PER_PAGE,
  LicenseSearchFilter,
  LicenseSearchResponse,
  LicenseSearchResult,
  LicenseSlice
} from './types';
import { isEmpty, trim } from 'lodash';
import { formatLicenseSearchResponse } from './utils';

const initialState: LicenseSlice = {
  isLicenseFailure: false,
  isLicenseLoading: false,
  isLicenseSuccess: false,
  searchFilter: {},
  searchResult: [],
  searchResultTotalCount: 0,
  sortField: undefined,
  sortOrder: 'asc',
  currentPage: 1,
  totalPages: 1,
  exportURL: '',
  isDownloadReady: false,
  searchResultRaw: [],
  filterStartingFields: []
};

const splitName = (name: string) => {
  const trimmedName = trim(name || '');
  if (!trimmedName) {
    return { firstName: '', lastName: '' };
  }
  
  // Split on whitespace and filter out empty parts
  const parts = trimmedName.split(/\s+/).filter(part => part.length > 0);
  const firstName = parts[0] || '';
  const lastName = parts.slice(1).join(' ') || '';
  return { firstName, lastName };
};

export const formatFilter = (form: Record<string, any>): LicenseSearchFilter => {
  const formatField = (field: string, fallback: string = '') => trim(form[field]) || fallback;

  const { firstName, lastName } = splitName(form.name);

  return {
    organizationName: formatField('organizationName'),
    branchCode: formatField('branchCode'),
    firstName: formatField('firstName', firstName),
    lastName: formatField('lastName', lastName),
    profileCode: formatField('profileCode'),
    licensedProvince: formatField('licensedProvince'),
    name: formatField('name'),
    legalName: formatField('legalName'),
    licenseNumber: formatField('licenseNumber'),
    status: formatField('status'),
    screeningStatus: formatField('screeningStatus'),
    expiredDateStart: form.expiredDateStart ? new Date(form.expiredDateStart) : null,
    expiredDateEnd: form.expiredDateEnd ? new Date(form.expiredDateEnd) : null
  };
};

export const fetchLicenses = createAsyncThunk(
  LINKS.LICENSE_SEARCH,
  async (
    payload: {
      filter?: LicenseSearchFilter;
      page?: number;
      sortOrder?: 'asc' | 'desc';
      sortField?: string;
      isNewSearch?: boolean;
      lang?: string;
    },
    { getState, dispatch }: { getState: () => RootStateOrAny; dispatch }
  ) => {
    const { sortField, sortOrder, currentPage, searchFilter } = payload.isNewSearch
      ? initialState
      : getState()?.license;
    const targetPage = payload.page || currentPage;
    const searchOffset = Math.max(DEFAULT_OFFSET, (targetPage - 1) * LICENSE_TABLE_LENGTH_PER_PAGE);
    const filter = isEmpty(payload.filter) ? searchFilter : payload.filter;

    return apiClient(dispatch, getState).post(LINKS.LICENSE_SEARCH, filter, {
      params: {
        sortField: payload.sortField || sortField,
        sortOrder: payload.sortOrder || sortOrder,
        offset: searchOffset,
        limit: DEFAULT_LIMIT,
        lang: payload.lang || 'en' // inclue lang in query params
      }
    });
  }
);
export const exportLicenses = createAsyncThunk(
  LINKS.LICENSE_EXPORT,
  async (payload: undefined, { getState, dispatch }: { getState: () => RootStateOrAny; dispatch }) => {
    const { sortField, sortOrder, searchFilter } = getState()?.license;

    return apiClientForDownload(dispatch, getState)
      .post(LINKS.LICENSE_EXPORT, searchFilter, {
        responseType: 'blob',
        params: { sortField, sortOrder }
      })
      .then((res: any) => {
        // TODO: may want to do data compress+decompress for later if data set grows
        const url = window.URL.createObjectURL(new Blob([res.data]));

        return url;
      });
  }
);

export const licenseSlice = createSlice({
  name: 'license',
  initialState,
  reducers: {
    resetState: () => {
      return initialState;
    },
    saveStartingFieldsArr: (state, action) => {
      state.filterStartingFields = action.payload;
    }
  },
  extraReducers: builder => {
    builder
      .addCase(fetchLicenses.pending, (state, action) => {
        if (action?.meta?.arg?.filter) {
          state.searchFilter = action?.meta?.arg?.filter;
        }

        state.sortField = action?.meta?.arg?.sortField || state.sortField;
        state.sortOrder = action?.meta?.arg?.sortOrder || 'asc';

        state.isLicenseLoading = true;
        state.isLicenseSuccess = false;
        state.isLicenseFailure = false;
      })
      .addCase(fetchLicenses.fulfilled, (state, action) => {
        const { isNewSearch, page } = action?.meta?.arg || {};
        const totalCount = (action?.payload as unknown as LicenseSearchResponse)?.totalCount || 0;
        const fetchedLicenses: LicenseSearchResult[] = (action?.payload as unknown as LicenseSearchResponse)?.licenses;

        const formattedLicenses = formatLicenseSearchResponse(fetchedLicenses);

        state.searchResultRaw = fetchedLicenses;
        state.searchResult = formattedLicenses;
        state.searchResultTotalCount = totalCount;
        state.totalPages = Math.ceil(totalCount / LICENSE_TABLE_LENGTH_PER_PAGE);
        state.currentPage = isNewSearch ? 1 : page || state.currentPage;

        state.isLicenseLoading = false;
        state.isLicenseSuccess = true;
        state.isLicenseFailure = false;
      })
      .addCase(fetchLicenses.rejected, state => {
        state.searchResult = [];
        state.searchResultTotalCount = 0;
        state.totalPages = 1;
        state.currentPage = 1;
        state.sortField = null;

        state.isLicenseLoading = false;
        state.isLicenseSuccess = false;
        state.isLicenseFailure = true;
      })
      .addCase(exportLicenses.pending, state => {
        state.isLicenseLoading = true;
        state.isLicenseSuccess = false;
        state.isLicenseFailure = false;
        state.isDownloadReady = false;
      })
      .addCase(exportLicenses.fulfilled, (state, action) => {
        state.exportURL = action?.payload;
        state.isLicenseLoading = false;
        state.isLicenseSuccess = true;
        state.isLicenseFailure = false;
        state.isDownloadReady = true;
      })
      .addCase(exportLicenses.rejected, state => {
        state.isLicenseLoading = false;
        state.isLicenseSuccess = false;
        state.isLicenseFailure = true;
        state.isDownloadReady = false;
      });
  }
});

export const selectLicenseLoading = state => state.license.isLicenseLoading;
export const selectLicenseSuccess = state => state.license.isLicenseSuccess;
export const selectLicenseFailure = state => state.license.isLicenseFailure;
export const selectSearchFilter = state => state.license.searchFilter;
export const selectSearchResult = state => state.license.searchResult;
export const selectSearchResultRaw = state => state.license.searchResultRaw;
export const selectSearchResultTotalCount = state => state.license.searchResultTotalCount;
export const selectIsDownloadReady = state => state.license.isDownloadReady;
export const selectExportURL = state => state.license.exportURL;
export const selectCurrentPage = state => state.license.currentPage;
export const selectTotalPages = state => state.license.totalPages;
export const selectSortOrder = state => state.license.sortOrder;
export const selectSortField = state => state.license.sortField;
export const selectStartingFieldsArr = state => state.license.filterStartingFields;

// Helper function to determine if license data is ready for display
const isLicenseDataReady = (isLoading: boolean, displayData: any[], rawData: any[]): boolean => {
  return !isLoading && 
         !!displayData && 
         !!rawData && 
         displayData.length === rawData.length;
};

// Helper function to get safe data values when data is ready
const getSafeDataValue = <T>(isReady: boolean, value: T, fallback: T): T => {
  return isReady ? value : fallback;
};

// Helper function to extract license state values
const extractLicenseState = (state: RootStateOrAny) => {
  const licenseState = state.license || {};
  return {
    isLoading: licenseState.isLicenseLoading || false,
    displayData: licenseState.searchResult || [],
    rawData: licenseState.searchResultRaw || [],
    totalCount: licenseState.searchResultTotalCount || 0,
    currentPage: licenseState.currentPage || 1,
    totalPages: licenseState.totalPages || 1,
    sortOrder: licenseState.sortOrder || 'asc',
    sortField: licenseState.sortField || undefined
  };
};

// Combined selector to prevent race conditions between display and raw data
export const selectLicenseTableData = (state: RootStateOrAny) => {
  const licenseState = extractLicenseState(state);
  const { isLoading, displayData, rawData, totalCount, currentPage, totalPages, sortOrder, sortField } = licenseState;
  
  const isDataReady = isLicenseDataReady(isLoading, displayData, rawData);

  return {
    isLoading,
    isDataReady,
    displayData: getSafeDataValue(isDataReady, displayData, []),
    rawData: getSafeDataValue(isDataReady, rawData, []),
    totalCount: getSafeDataValue(isDataReady, totalCount, 0),
    currentPage: getSafeDataValue(isDataReady, currentPage, 1),
    totalPages: getSafeDataValue(isDataReady, totalPages, 1),
    sortOrder: getSafeDataValue(isDataReady, sortOrder, 'asc'),
    sortField: getSafeDataValue(isDataReady, sortField, undefined),
    canExport: isDataReady && displayData.length > 0
  };
};

export const { resetState, saveStartingFieldsArr } = licenseSlice.actions;
export default licenseSlice.reducer;
